import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { I4BGridFilterButtonType, I4BGridFilterOptions } from '@iot-platform/models/grid-engine';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent, IRowNode, RowClickedEvent } from 'ag-grid-community';
import { TranslationDictionary } from '../../models/translation-dictionary.model';

@Component({
  selector: 'i18n-translation-table-view',
  templateUrl: './translation-table-view.component.html',
  styleUrls: ['./translation-table-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AgGridAngular]
})
export class TranslationTableViewComponent implements OnChanges, OnInit {
  @Input() data: TranslationDictionary[] = [];
  @Input() editMode: boolean;
  @Input() languages: string[] = [];

  @Output() gridReady: EventEmitter<{ gridApi: GridApi }> = new EventEmitter<{ gridApi: GridApi }>();
  @Output() rowClicked: EventEmitter<RowClickedEvent> = new EventEmitter<RowClickedEvent>();
  @Output() selectionChanged: EventEmitter<IRowNode[]> = new EventEmitter<IRowNode[]>();

  gridOptions: GridOptions;
  columnDefs: ColDef[];
  components;

  gridApi: GridApi;
  keyFieldId = 'key';

  readonly textColumnFilterDef: Partial<ColDef> = {
    filter: 'agTextColumnFilter',
    filterParams: {
      maxNumConditions: 1,
      filterOptions: [I4BGridFilterOptions.CONTAINS, I4BGridFilterOptions.NOT_CONTAINS, I4BGridFilterOptions.START_WITH, I4BGridFilterOptions.END_WITH],
      buttons: [I4BGridFilterButtonType.RESET]
    }
  };

  constructor() {
    this.gridOptions = {
      headerHeight: 30,
      defaultColDef: {
        cellStyle: { height: '100%', display: 'flex ', 'align-items': 'center ' },
        editable: false,
        sortable: true,
        flex: 1,
        filter: true,
        resizable: true
      },
      tooltipShowDelay: 0,
      pagination: false,
      rowHeight: 40,
      rowSelection: {
        mode: 'multiRow',
        copySelectedRows: true,
        // restrict row selections via checkbox selection
        enableClickSelection: true,
        checkboxes: false,
        headerCheckbox: false
      },
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      onRowClicked: (event: RowClickedEvent) => this.onRowClicked(event),
      onSelectionChanged: () => this.onSelectionChanged()
    };

    this.components = {};
  }

  public ngOnInit(): void {
    this.initColumnDefs();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.editMode && changes.editMode.previousValue !== null) {
      this.handleEditMode();
    }
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
    this.gridReady.emit({
      gridApi: params.api
    });
  }

  public initColumnDefs(): void {
    const columnDefs: ColDef[] = [
      {
        colId: this.keyFieldId,
        headerName: this.keyFieldId.toUpperCase(),
        field: this.keyFieldId,
        // AG Grid 36.2: `tooltipField` is deprecated — `tooltip: true` shows the displayed cell value (= `field`, no formatter)
        tooltip: true,
        ...this.textColumnFilterDef
      }
    ];
    this.languages.forEach((lang: string) => {
      columnDefs.push({
        colId: lang,
        headerName: lang.toUpperCase(),
        field: `${lang}.value`,
        tooltip: true,
        cellClass: (params) => (params && params.data && params.data[lang] && params.data[lang].missing ? 'bg-red' : ''),
        ...this.textColumnFilterDef
      });
    });
    this.columnDefs = columnDefs;
  }

  public handleEditMode(): void {
    if (this.gridApi) {
      // Toggle checkbox selection
      const columnDefs: ColDef[] = [...this.gridApi.getColumnDefs()];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.gridOptions.rowSelection as any).checkboxes = this.editMode;
      // columnDefs[0].checkboxSelection = this.editMode;
      this.gridApi.updateGridOptions({ columnDefs });

      // Deselect all rows when read only mode
      if (!this.editMode) {
        this.gridApi.stopEditing();
        this.gridApi.deselectAll();
      }

      // Refresh key cells
      const params = {
        force: true,
        suppressFlash: false,
        columns: [this.keyFieldId]
      };
      this.gridApi.refreshCells(params);
    }
  }

  public onRowClicked(event): void {
    this.rowClicked.emit(event);
  }

  public onSelectionChanged(): void {
    this.selectionChanged.emit(this.gridApi.getSelectedNodes());
  }
}
